import { Search } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  TextField,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  Hidden,
  Stack,
  Button,
} from '@mui/material';
import {
  GenVehicleDetails,
  WOVehicleListModal,
} from '@pinnacleautomotive/clarity-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NewOrderTypes } from '../../../../common/enums/enums';
import EditVehicleModal from '../../../../components/EditVehicleModal';
import GenAlert from '../../../../components/GenAlert';
import { useLazyGetFrontLineVehiclesListQuery } from '../../../../redux/rtk/vendors.services';
import { currentUserCompanyId } from '../../../../redux/selectors/userDataSelectors';
import { getSelectorActionsBasedOnType } from '../helper';
import VehicleNotFound from './components/VehicleNotFound';

const joinGrammatically = (list, and) => {
  if (!list.length) return null;
  if (list.length === 1) return `${list[0]}`;
  const lastItem = list.pop();

  return `${list.join(', ')} ${and} ${lastItem}`;
};

export default function Vehicle({
  onValidStep,
  boxProps = { p: { xs: 2, md: 6 }, mb: 12 },
  divider = <Divider />,
  orderType = NewOrderTypes.SIMPLE_ORDER,
}) {
  const { t } = useTranslation(['orders', 'global']);
  const dispatch = useDispatch();
  const [vinSearch, setVinSearch] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [vehicleToEdit, setVehicleToEdit] = useState({});
  const {
    addVehicleDetailsAction,
    clearErrorAction,
    closeEditVehicleModalAction,
    editVehicleDetailsAction,
    getVehicleByVinAction,
    openEditVehicleModalAction,
    eitVehicleDataErrorSelector,
    editVehicleModalOpenSelector,
    isLoadingVehicleSelector,
    selectedDepartmentSelector,
    selectedVehicleSelector,
    vehicleNotFoundSelector,
    getAllDealerSelector,
    setStockNumberForVehicle,
    getVehicleId,
  } = getSelectorActionsBasedOnType(orderType);
  const vehicle = useSelector(selectedVehicleSelector);
  const vehicleNotFound = useSelector(vehicleNotFoundSelector);
  const editOpened = useSelector(editVehicleModalOpenSelector);
  const selectedDepartment = useSelector(selectedDepartmentSelector);
  const editVehicleDataError = useSelector(eitVehicleDataErrorSelector);
  const vehicleId = useSelector(getVehicleId);
  const [vinSelected, setVinSelected] = useState(null);
  const [vehiculeDataMissingError, setVehiculeDataMissingError] =
    useState(false);
  const loading = useSelector(isLoadingVehicleSelector);
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const allDealers = useSelector(getAllDealerSelector);
  const userCompany = useSelector(currentUserCompanyId);
  const selectedDealer = allDealers?.filter(
    item => item?.companyId === userCompany
  )?.[0];
  const isFrontLineIdsEnabled = selectedDealer?.frontlineDealers?.length > 0;
  const roofTops = selectedDealer?.frontlineDealers || [];
  const [openVehicleListModal, setOpenVehicleListModal] = useState(false);
  const [fetchVehicles, { data, isLoading, isFetching }] =
    useLazyGetFrontLineVehiclesListQuery();

  const vehicleActionByStateFound = {
    true: vehicleData =>
      dispatch(
        editVehicleDetailsAction({
          vehicleId: vehicle.vehicleId,
          data: vehicleData,
        })
      ),
    false: vehicleData =>
      dispatch(addVehicleDetailsAction({ data: vehicleData })),
  };

  useEffect(() => {
    if (vinSelected && vinSelected.vin && vehicleId) {
      const vehicleData = {
        make: vinSelected?.make,
        model: vinSelected?.model,
        year: vinSelected?.year,
        color: vinSelected?.colorCode,
        custody: {
          stockNumber: vinSelected?.stockNumber,
        },
        vin: vinSelected?.vin,
      };
      dispatch(editVehicleDetailsAction({ data: vehicleData, vehicleId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vinSelected, vehicleId]);

  useEffect(
    () => onValidStep(!!vehicle && !vehiculeDataMissingError),
    [onValidStep, vehicle, vehiculeDataMissingError]
  );

  useEffect(() => {
    if (!vehicle || !selectedDepartment) return;
    const stockNumberInvalid =
      selectedDepartment?.setting?.orders?.requiredFields.stockNumber &&
      !vehicle.custody?.stockNumber &&
      t('global:stock');

    const poInvalid =
      selectedDepartment?.setting?.orders?.requiredFields.purchaseOrder &&
      !vehicle.po &&
      t('global:po');

    const roInvalid =
      selectedDepartment?.setting?.orders?.requiredFields.repairOrder &&
      !vehicle.ro &&
      t('global:ro');

    setVehiculeDataMissingError(
      joinGrammatically(
        [stockNumberInvalid, poInvalid, roInvalid].filter(error => error),
        t('global:and')
      )
    );
  }, [selectedDepartment, t, vehicle]);

  const searchVehicle = () => {
    setVinSelected(null);
    dispatch(getVehicleByVinAction({ vin: vinSearch }));
  };

  const openEditVehicle = () => {
    dispatch(openEditVehicleModalAction());
    setModalTitle(t('newOrder.editVehicle'));
    setVehicleToEdit(vehicle);
  };

  const openAddVehicle = () => {
    dispatch(openEditVehicleModalAction());
    setModalTitle(t('newOrder.addVehicle'));
    setVehicleToEdit({ vin: vinSearch });
  };

  const cancelEditVehicle = () => {
    dispatch(closeEditVehicleModalAction());
    setVehicleToEdit({});
  };

  const clearSaveVehiculeError = () => {
    dispatch(clearErrorAction());
  };

  const onSaveEditVehicle = vehicleData => {
    vehicleActionByStateFound[!vehicleNotFound](vehicleData);
  };

  const handleSelectVehicle = selectedVehicle => {
    setVinSearch(selectedVehicle?.vin);
    setVinSelected(selectedVehicle);
    dispatch(setStockNumberForVehicle(selectedVehicle?.stockNumber));
    dispatch(getVehicleByVinAction({ vin: selectedVehicle?.vin }));
  };

  const handleSearch = (search, rooftop) => {
    fetchVehicles({
      companyId: rooftop,
      searchTerm: search,
    });
  };

  const openVehicleLists = () => {
    setOpenVehicleListModal(true);
  };

  const vinHasError = vinSearch && !/^[A-Z\d]+$/g.test(vinSearch);

  return (
    <>
      <EditVehicleModal
        open={editOpened}
        title={modalTitle}
        onClose={cancelEditVehicle}
        vehicleDetails={vehicleToEdit}
        onSave={onSaveEditVehicle}
        loading={loading}
        department={selectedDepartment}
      />
      <WOVehicleListModal
        open={openVehicleListModal}
        onClose={() => setOpenVehicleListModal(false)}
        vehicles={data?.data}
        rooftops={roofTops}
        onSearch={handleSearch}
        onSelectVehicle={handleSelectVehicle}
        loading={isLoading || isFetching}
      />
      <Box {...boxProps}>
        <Box>
          <Typography variant="h6" mb={6}>
            {t('newOrder.vehicle.title')}
          </Typography>
        </Box>
        <Box
          display="flex"
          pb={{ xs: 0, md: 5 }}
          pr={{ xs: 0, md: 2 }}
          alignItems="center"
        >
          <TextField
            size={(smallScreen && 'small') || 'medium'}
            label={`${t('vinNumber')}: (*)`}
            value={vinSearch}
            inputProps={{ 'data-testid': 'search-input', maxLength: 50 }}
            onChange={event => setVinSearch(event.target.value.toUpperCase())}
            error={vinHasError}
            helperText={vinHasError && t('newOrder.vehicle.helperText')}
            sx={{
              '& input': {
                bgcolor: 'background.paper',
              },
              '& .MuiFormHelperText-root': {
                position: 'absolute',
                textWrap: 'nowrap',
                left: '-10px',
                bottom: '-25px',
              },
              marginRight: { xs: 1, sm: '2rem' },
              flex: '3 1 auto',
            }}
          />
          <Stack flexDirection="row" gap={2}>
            <LoadingButton
              size="large"
              variant="outlined"
              startIcon={<Search />}
              disabled={!vinSearch || vinHasError}
              onClick={searchVehicle}
              loading={loading}
              sx={{
                px: { xs: '7px', sm: '21px' },
                minWidth: { xs: 'unset', sm: '64px' },
                '& .MuiButton-startIcon': {
                  ml: { xs: 0, sm: '-4px' },
                  mr: { xs: 0, sm: '8px' },
                },
              }}
            >
              {(!smallScreen && t('search', { ns: 'global' })) || ''}
            </LoadingButton>
            {isFrontLineIdsEnabled && (
              <Button variant="contained" onClick={openVehicleLists}>
                {t('newOrder.vehicle.listVehicles', { ns: 'orders' })}
              </Button>
            )}
          </Stack>
        </Box>
        <Hidden mdDown>{divider}</Hidden>
        <Box pt={3} mb={4}>
          {vehicle && (
            <GenVehicleDetails
              vehicle={vehicle}
              showEdit={true}
              onClickEdit={openEditVehicle}
            />
          )}
          {vehicleNotFound && (
            <VehicleNotFound onAddDetailsClick={openAddVehicle} />
          )}
        </Box>
        <GenAlert
          severity="error"
          open={editVehicleDataError}
          onClose={clearSaveVehiculeError}
        >
          {t('newOrder.vehicle.snackBarError')}
        </GenAlert>
        <GenAlert severity="warning" open={!!vehiculeDataMissingError}>
          {t('dataVehicleMissing', { missing: vehiculeDataMissingError })}
        </GenAlert>
      </Box>
    </>
  );
}
